import { warnInDev } from "../debug";
import InputState from "../engine/inputActions/InputState";
import { RDisplay } from "../ui/RotDisplay";
import { Commands } from "./commands/commands";
import { buildState, MAP_HEIGHT, MAP_WIDTH } from "./gameState";
import { Bounds } from "../engine/space/bounds";
import EntityManager from "./EntityManager";
import { Action, runAction } from "./actions/Action";
import AsyncManager from "./AsyncManager";
import { createDMap } from "../lib/dMap";
import { moveToEmpty } from "./map/pathfinding";
import { generateMap } from "./map/generation/generation";
import { ParticleManager } from "./anims/particleSystem";
import { endOfTurnEffects } from "./actors/systems/Regen";
import { clearLog, log } from "../engine/logger/logger";
import {
  LookParticle,
  LookParticleBreed,
} from "./anims/particles/LookParticle";
import { piecePosByCoord } from "./words/wordUtils";
import { pickupItem } from "./items/itemUtils";
import { BasicMap } from "./map/BasicMap";
import { SoundSystem } from "../sounds";

export let gameData = buildState();

export const initializeState = () => {
  clearLog();
  log(`Welcome to Spellgeon.`);
  gameData = buildState();
  InputState.pushKeyHandler([
    "baseActions",
    "playerActions",
    "graphMovement",
    "letters",
    "advance",
    "cancel",
  ]);
  InputState.pushInputHandler(["base", "move", "letters", "playerActions"]);

  const rng = gameData.seedRng.getRNG();
  generateMap(rng, gameData.current);
  gameData.dMapPlayer = createDMap(
    gameData.player.pieces.asArray(),
    moveToEmpty,
    20
  );
  gameData.focus = ParticleManager.createParticle(
    LookParticleBreed,
    gameData.player.x,
    gameData.player.y
  ) as LookParticle<void>;
};

const mapArrows = (key: string) => {
  return key.startsWith("Arrow") ? `S${key}` : key;
};

export const handleInput = (key: string, shiftKey: boolean) => {
  if (shiftKey) {
    key = mapArrows(key);
  }

  if (!gameData.actionPending) {
    const actionResult = InputState.keyToAction(key);
    if (actionResult) {
      const [actionName, args] = actionResult;
      const result = Commands[actionName](args);
      if (result instanceof Promise) {
        gameData.actionPending = true;
        // Some actions need to be async (require additional user input)
        result.then(async (r) => {
          const waitedResult = await result;
          if (waitedResult) {
            runPlayerAction(waitedResult);
          }
          gameData.actionPending = false;
        });
      } else if (result) {
        gameData.actionPending = true;
        //Commands only have action results when they're a turn causing action
        runPlayerAction(result).then(() => {
          gameData.actionPending = false;
        });
      }
      return true;
    } else {
      warnInDev(`No action for input: ${key}`);
    }
  }
  return false;
};

export const runPlayerAction = async (
  action: Action<any> | Array<Action<any>>
) => {
  startTurn();
  if (Array.isArray(action)) {
    console.warn("given multiple actions but the handler was not written");
    //TODO: This format doesn't wait on actions
    action.forEach((a) => {
      runAction(a);
    });
  } else {
    await runAction(action);
  }
  await AsyncManager.waitForPending();
  advanceTurn();
};

export const startTurn = () => {
  ParticleManager.clearStartOfTurn();
};

export const advanceTurn = async () => {
  await EntityManager.runEntityQueue();
  gameData.dMapPlayer = createDMap(
    gameData.player.pieces.asArray(),
    moveToEmpty,
    80
  );
  ParticleManager.clearEndOfTurn();
  endOfTurnEffects();
  playerCollectItemsOnEmpty();
};

export const drawWorld = (display: RDisplay, bounds: Bounds, view: Bounds) => {
  gameData.current.draw(display, bounds, view);
  gameData.gibs.forEach((particle) => {
    particle.render(display, bounds, view);
  });
  EntityManager.drawEntities(display, bounds, view);
  gameData.particles.forEach((particle) => {
    particle.render(display, bounds, view);
  });
};

export const findActorForPosition = (x: number, y: number) => {
  const piece = gameData.pieces.get(piecePosByCoord(x, y));
  if (piece) {
    return gameData.actors.get(piece.owner);
  }
};

export const getItemsForPosition = (x: number, y: number) => {
  return gameData.items.filter((item) => item.x === x && item.y === y);
};

export const playerCollectItemsOnEmpty = async () => {
  if (
    gameData.player.alive &&
    gameData.actors.size === 1 &&
    !gameData.actors.find((a) => a !== gameData.player)
  ) {
    let anyUsed = false;
    let anyPicked = false;
    gameData.items.forEach((item) => {
      if (item.breed.collectOnEmpty) {
        if (!item.breed.furniture) {
          anyPicked = true;
          pickupItem(gameData.player, item);
        } else {
          item.useItem(gameData.player);
          anyUsed = true;
        }
      }
    });
    if (anyPicked) {
      SoundSystem.play("pickup");
    } else if (anyUsed) {
      SoundSystem.play("powerup");
    }
  }
};

export const changeLevel = () => {
  SoundSystem.play("stairs");
  gameData.items.clear();
  gameData.actors.clear();
  gameData.gibs.clear();
  gameData.particles.clear();
  gameData.pieces.clear();
  gameData.freeEntry = false;
  gameData.noWordTriggers = false;
  gameData.player.pieces.forEach((p) => {
    p.end();
  });

  const playerWord = gameData.player.pieces.map((p) => p.ch).join("");

  gameData.player.pieces.clear();

  gameData.actors.add(gameData.player.id, gameData.player);
  gameData.current = new BasicMap(
    MAP_WIDTH,
    MAP_HEIGHT,
    gameData.seedRng.getGenerator(),
    gameData.current.depth + 1
  );

  generateMap(
    gameData.current.rng.getRNG(),
    gameData.current,
    gameData.player,
    playerWord || "PLAYER"
  );

  if (gameData.focus) {
    gameData.focus.x = gameData.player.x;
    gameData.focus.y = gameData.player.y;
    gameData.particles.add(gameData.focus.id, gameData.focus);
  }
};
